import React from "react";
import { Controller, useFormState } from "react-hook-form";
import styled from "styled-components";
import colors from "../../common/colors";
import { fontSize, space } from "../../common/variables";
import { getValidationResultFromFormError } from "../../common/validations";
import Checkbox from "../../components/Checkbox";
import FormDevTool from "../../components/FormDevTool";
import { FormField, FormFieldSet } from "../../components/FormField";
import FormInvalidError from "../../components/FormInvalidError";
import { CellBlockFooter } from "../../components/Grid/CellBlock";
import TextInput from "../../components/TextInput";
import {
    ORDERER_ADMIN_ROLE_DESCRIPTION,
    ORDERER_ROLE_DESCRIPTION,
} from "../AdministratorView/administratorDetailsConfig";
import { OrdererInfoFormData, useOrdererInfoForm } from "./OrdererDetailsView.hooks";
import { OrdererAccountWideErrors } from "./ordererDetailsConfig";

export interface OrdererInfoData {
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
    customerCreationAllowed: boolean;
    municipalityRole: "ORDERER" | "ORDERER_ADMIN";
}

interface Props extends ReturnType<typeof useOrdererInfoForm> {
    onCancel: () => void;
    onSubmit: (data: OrdererInfoFormData) => void;
    isNewForm?: boolean;
    isSubmitLoading?: boolean;
    disableSubmit?: boolean;
    showRoleSelect?: boolean;
    accountWideErrors?: OrdererAccountWideErrors;
}

const OrdererInfoForm = (props: Props) => {
    const { isValid, isDirty } = useFormState({ control: props.control });

    return (
        <form onSubmit={props.handleSubmit(props.onSubmit)}>
            <FormFieldSet>
                <FormField $halfSize>
                    <Controller
                        name="firstName"
                        control={props.control}
                        rules={{ required: true }}
                        render={({ field: { onBlur, onChange, value }, fieldState }) => (
                            <TextInput
                                onBlur={onBlur}
                                onChange={onChange}
                                value={value}
                                required
                                autoComplete="on"
                                placeholder="Förnamn"
                                validation={getValidationResultFromFormError(fieldState)}
                            />
                        )}
                    />
                </FormField>
                <FormField $halfSize>
                    <Controller
                        name="lastName"
                        control={props.control}
                        rules={{ required: true }}
                        render={({ field: { onBlur, onChange, value }, fieldState }) => (
                            <TextInput
                                onBlur={onBlur}
                                onChange={onChange}
                                value={value}
                                required
                                autoComplete="on"
                                placeholder="Efternamn"
                                validation={getValidationResultFromFormError(fieldState)}
                            />
                        )}
                    />
                </FormField>
            </FormFieldSet>
            <FormFieldSet>
                <FormField $halfSize>
                    <Controller
                        name="phone"
                        control={props.control}
                        rules={{ required: true }}
                        render={({ field: { onBlur, onChange, value }, fieldState }) => (
                            <TextInput
                                onBlur={onBlur}
                                onChange={onChange}
                                value={value}
                                autoComplete="on"
                                required
                                placeholder="Telefon"
                                validation={getValidationResultFromFormError(fieldState)}
                            />
                        )}
                    />
                </FormField>
                <FormField $halfSize>
                    <Controller
                        name="email"
                        control={props.control}
                        rules={{ required: true }}
                        render={({ field: { onBlur, onChange, value }, fieldState }) => (
                            <TextInput
                                onBlur={onBlur}
                                onChange={onChange}
                                value={value}
                                autoComplete="on"
                                required
                                placeholder="E-post"
                                validation={
                                    props.accountWideErrors?.email
                                        ? {
                                              isDirty: true,
                                              isValid: false,
                                              message: props.accountWideErrors.email,
                                          }
                                        : getValidationResultFromFormError(fieldState)
                                }
                            />
                        )}
                    />
                </FormField>
            </FormFieldSet>
            {props.showRoleSelect && (
                <FormField>
                    <Controller
                        name="municipalityRole"
                        control={props.control}
                        render={({ field: { onChange, value }, fieldState }) => (
                            <RoleGroup>
                                <RoleLegend>Roll</RoleLegend>
                                <RoleOption>
                                    <input
                                        type="radio"
                                        name="municipalityRole"
                                        checked={value === "ORDERER"}
                                        onChange={() => onChange("ORDERER")}
                                        aria-describedby="orderer-role-description"
                                    />
                                    <RoleOptionText>
                                        <RoleOptionTitle>Beställare</RoleOptionTitle>
                                        <RoleOptionHint id="orderer-role-description">
                                            {ORDERER_ROLE_DESCRIPTION}
                                        </RoleOptionHint>
                                    </RoleOptionText>
                                </RoleOption>
                                <RoleOption>
                                    <input
                                        type="radio"
                                        name="municipalityRole"
                                        checked={value === "ORDERER_ADMIN"}
                                        onChange={() => onChange("ORDERER_ADMIN")}
                                        aria-describedby="orderer-admin-role-description"
                                    />
                                    <RoleOptionText>
                                        <RoleOptionTitle>
                                            Beställare administratör
                                        </RoleOptionTitle>
                                        <RoleOptionHint id="orderer-admin-role-description">
                                            {ORDERER_ADMIN_ROLE_DESCRIPTION}
                                        </RoleOptionHint>
                                    </RoleOptionText>
                                </RoleOption>
                                {fieldState.error?.message && (
                                    <RoleError>{fieldState.error.message}</RoleError>
                                )}
                            </RoleGroup>
                        )}
                    />
                </FormField>
            )}
            <FormField $noMargin>
                <Controller
                    name="customerCreationAllowed"
                    control={props.control}
                    render={({ field: { onChange, value } }) => (
                        <Checkbox
                            onChange={onChange}
                            value={value}
                            text="Får lägga till nya slutkunder"
                        />
                    )}
                />
                {props.accountWideErrors?.customerCreationAllowed && (
                    <CheckboxError>
                        <FormInvalidError
                            error={props.accountWideErrors.customerCreationAllowed}
                        />
                    </CheckboxError>
                )}
            </FormField>
            <FormDevTool control={props.control} />
            <CellBlockFooter
                disableSubmit={props.disableSubmit || !isValid || !isDirty}
                hasChangesText={
                    !props.isNewForm && isDirty
                        ? "Förmuläret har osparade ändringar."
                        : undefined
                }
                marginTop
                isLoading={props.isSubmitLoading}
                onCancel={props.onCancel}
                submitButtonType="submit"
            />
        </form>
    );
};

const CheckboxError = styled.div`
    margin-top: ${space.small};
    font-size: ${fontSize.small};
`;

const RoleGroup = styled.fieldset`
    display: flex;
    flex-direction: column;
    gap: ${space.small};
    margin: 0;
    padding: 0;
    border: none;
`;

const RoleLegend = styled.legend`
    padding: 0;
    margin-bottom: ${space.small};
    font-size: ${fontSize.small};
    color: ${colors.black};
`;

const RoleOption = styled.label`
    display: flex;
    align-items: flex-start;
    gap: ${space.small};
    font-size: ${fontSize.small};
    color: ${colors.black};
    cursor: pointer;

    input {
        margin-top: 2px;
    }
`;

const RoleOptionText = styled.span`
    display: flex;
    flex-direction: column;
    gap: ${space.xxSmall};
`;

const RoleOptionTitle = styled.span`
    font-size: ${fontSize.small};
    color: ${colors.black};
`;

const RoleOptionHint = styled.span`
    font-size: ${fontSize.xSmall};
    color: ${colors.grayDarker};
`;

const RoleError = styled.span`
    color: ${colors.red};
    font-size: ${fontSize.small};
`;

export default OrdererInfoForm;
